import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useRestaurantContext } from './RestaurantContext';
import { getActiveQueueEntry } from '../services/api/restApiAdapter';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';

const QueueContext = createContext();

export const useQueueContext = () => useContext(QueueContext);

export const QueueProvider = ({ children }) => {
  const { currentUser, isAuthLoading } = useAuth();
  const { restaurantId } = useRestaurantContext();
  const [activeQueue, setActiveQueue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQueueStatus = useCallback(async () => {
    if (!restaurantId) return;
    
    try {
      const response = await getActiveQueueEntry(restaurantId);
      if (response.success && response.data) {
        // Response data is an array of entries (usually just 1 for active)
        const entries = response.data;
        if (entries.length > 0) {
          setActiveQueue(entries[0]);
        } else {
          setActiveQueue(null);
        }
      } else {
        setActiveQueue(null);
      }
    } catch (err) {
      console.error('Error fetching queue status:', err);
      setError('Failed to fetch queue status');
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId]);

  // Initial fetch on mount or auth/restaurant change
  useEffect(() => {
    if (isAuthLoading || !restaurantId) return;
    
    fetchQueueStatus();
  }, [fetchQueueStatus, isAuthLoading, restaurantId]);

  // Firestore real-time listener (trigger only)
  useEffect(() => {
    if (isAuthLoading || !restaurantId || !currentUser) {
      if (!isAuthLoading && !currentUser) {
        setActiveQueue(null);
        setIsLoading(false);
      }
      return;
    }

    try {
      const db = getFirestore();
      const queueRef = collection(db, 'restaurants', restaurantId, 'queuestatus');
      // Listen to all active entries (waiting/called) to detect when ANYONE's status changes
      // This allows us to re-calculate position immediately when someone else is seated.
      const q = query(queueRef, where('status', 'in', ['waiting', 'called']));

      let isInitialSnapshot = true;
      const unsubscribe = onSnapshot(q, (snapshot) => {
        // Skip the initial snapshot burst. Position and status are fetched via REST on mount.
        if (isInitialSnapshot) {
          isInitialSnapshot = false;
          return;
        }

        // If any document in the active queue collection changes (added, removed, or modified),
        // it means positions might have shifted. Trigger a refresh of authoritative data.
        if (snapshot.docChanges().length > 0) {
          fetchQueueStatus();
        }
      }, (err) => {
        console.error('Firestore queue listener error:', err);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('Failed to set up Firestore queue listener:', err);
    }
  }, [currentUser, isAuthLoading, restaurantId, fetchQueueStatus]);

  const value = {
    activeQueue,
    isLoading,
    error,
    refreshQueue: fetchQueueStatus
  };

  return <QueueContext.Provider value={value}>{children}</QueueContext.Provider>;
};
